import type { PublicInvoice } from '@arabat/contracts';
import { invoiceForScreen, invoiceFromJson, renderInvoiceHtml } from '@arabat/pos-core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InvoiceActions } from './InvoiceActions.js';
import { fontCss } from './invoice-document.js';

/**
 * An online order's invoice as a page (ADR-0138 amendments 7 and 8): the actions on top, the A4
 * document in a frame fitted to its height. `forStore` adds the store's actions (send to the
 * customer, share the PDF, call); `customerUrl` is the address the customer receives.
 */
export function InvoiceView({
  invoice,
  customerUrl,
  forStore,
}: {
  invoice: PublicInvoice;
  customerUrl: string;
  forStore: boolean;
}) {
  const { t } = useTranslation();
  const html = useMemo(
    () => invoiceForScreen(renderInvoiceHtml(invoiceFromJson(invoice), fontCss())),
    [invoice],
  );
  const frame = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(1200);
  const fit = () => {
    const doc = frame.current?.contentDocument;
    if (doc) setHeight(Math.ceil(doc.documentElement.scrollHeight) + 2);
  };
  useEffect(() => {
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, []);
  useEffect(() => {
    document.title = t('invoice.title', { number: invoice.number });
  }, [invoice.number, t]);
  return (
    // A long page with room below: browsers whose top bar hides while scrolling (WhatsApp's)
    // otherwise bounce at the bottom.
    <div className="min-h-dvh bg-canvas pb-32 [overscroll-behavior-y:none]">
      <header className="sticky top-0 z-10 border-b border-border bg-surface/95 backdrop-blur">
        <div className="mx-auto flex max-w-[860px] flex-wrap items-center justify-between gap-2 px-3 py-2">
          <p className="text-sm font-medium" dir="auto">
            {invoice.storeName} · <span dir="ltr">{invoice.number}</span>
          </p>
          <InvoiceActions
            html={() => Promise.resolve(html)}
            number={invoice.number}
            storeName={invoice.storeName}
            customerName={invoice.customerName}
            customerPhone={invoice.customerPhone}
            invoiceUrl={customerUrl}
            forStore={forStore}
          />
        </div>
      </header>
      <main className="mx-auto max-w-[860px] sm:p-4">
        <iframe
          ref={frame}
          title={t('invoice.title', { number: invoice.number })}
          srcDoc={html}
          onLoad={() => {
            fit();
            void frame.current?.contentDocument?.fonts?.ready.then(fit);
          }}
          className="block w-full border-0 bg-white sm:shadow"
          style={{ height }}
        />
      </main>
    </div>
  );
}
